import React, {useState, useMemo, useEffect, useCallback} from 'react';
import {
    Home,
    MapPin,
    TrendingUp,
    CheckCircle2,
    Phone,
    Image,
    Calendar,
    DollarSign,
    Maximize,
    Layers,
    ShieldCheck,
    Car,
    Coffee,
    Camera,
    History,
    X,
    ChevronRight,
    ChevronLeft,
    Sparkles,
    Hammer,
    Zap,
    Check,
    Calculator,
    FileText,
    HelpCircle,
    Send,
    Droplets,
    Compass,
    Bed,
    TreePine,
    Mountain,
} from 'lucide-react';

import {Link, useLocation} from 'react-router-dom';
import {IMaskInput} from "react-imask/esm";
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

export default function SellHouse() {
    const [selectedImageIndex, setSelectedImageIndex] = useState(null);
    const [galleryFilter, setGalleryFilter] = useState('all');
    const [activeTabFloor, setActiveTabFloor] = useState('first');
    const [openFaq, setOpenFaq] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [formSubmitted, setFormSubmitted] = useState(false);
    const [formData, setFormData] = useState({ name: '', phone: '', date: '', message: '' });

    // ROI Calculator States
    const [dailyRate, setDailyRate] = useState(130); // avg price per night in $
    const [occupancyDays, setOccupancyDays] = useState(200); // occupied days per year
    const [expenseRate, setExpenseRate] = useState(20); // operating expenses %

    // Gallery Data
    const galleryImages = [
        {
            id: 1,
            src: "/img/house/construction/1.jpeg",
            title: "Фасад будинку та власна ділянка",
            category: "exterior"
        },
        {
            id: 2,
            src: "/img/house/outside/1.webp",
            title: "Панорамний вид на гірські хребти",
            category: "exterior"
        },
        {
            id: 3,
            src: "/img/house/3d/4.webp",
            title: "Затишна відкрита тераса 7.8 м²",
            category: "exterior"
        },
        {
            id: 4,
            src: "/img/house/outside/septik-2.jpeg",
            title: "Септик на ділянці",
            category: "exterior"
        },
        {
            id: 5,
            src: "/img/house/construction/1.webp",
            title: "Фундамент та зведення каркасу будинка",
            category: "construction"
        },
        {
            id: 6,
            src: "/img/house/construction/2.webp",
            title: "Коробка будинку",
            category: "construction"
        },
        {
            id: 7,
            src: "/img/house/cover.jpeg",
            title: "Енергоефективні панорамні вікна",
            category: "construction"
        },
        {
            id: 8,
            src: "/img/house/plan/2.webp",
            title: "Зона спальні на другому поверсі",
            category: "interior"
        },
        {
            id: 9,
            src: "/img/house/plan/1.webp",
            title: "План першого поверха",
            category: "interior"
        },
        {
            id: 10,
            src: "/img/house/construction/facad-2.jpeg", //3.jpeg / 4.jpeg / 3.webp
            title: "Етап утеплення мінеральною ватою 200мм",
            category: "construction"
        },
        {
            id: 11,
            src: "/img/house/outside/zabor-1.jpeg",
            title: "Паркан та відкатні ворота",
            category: "exterior"
        },
        {
            id: 12,
            src: "/img/house/inside/1.webp",
            title: "Внутрішні роботи",
            category: "construction"
        },
        {
            id: 13,
            src: "/img/house/outside/3.jpeg",
            title: "Прокладка автономних комунікацій",
            category: "construction"
        },
        {
            id: 14,
            src: "/img/house/3d/2.webp",
            title: "3D Візуалізація ділянки з будинком",
            category: "exterior"
        },
        {
            id: 15,
            src: "/img/house/3d/3.webp",
            title: "3D Візуалізація фасаду будинка",
            category: "exterior"
        }
    ];

    const filteredGallery = useMemo(() => {
        return galleryFilter === 'all'
            ? galleryImages
            : galleryImages.filter(img => img.category === galleryFilter);
    }, [galleryFilter]);

    const handleNextImage = () => {
        if (selectedImageIndex !== null) {
            setSelectedImageIndex((selectedImageIndex + 1) % filteredGallery.length);
        }
    };

    const handlePrevImage = () => {
        if (selectedImageIndex !== null) {
            setSelectedImageIndex((selectedImageIndex - 1 + filteredGallery.length) % filteredGallery.length);
        }
    };

    // Calculations for ROI
    const housePrice = 71000;
    const estimatedFinishingCost = 15000; // estimated cost to finish 100% turnkey
    const totalInvestment = housePrice + estimatedFinishingCost;

    const grossAnnualIncome = useMemo(() => {
        return dailyRate * occupancyDays;
    }, [dailyRate, occupancyDays]);

    const netAnnualIncome = useMemo(() => {
        return Math.round(grossAnnualIncome * (1 - expenseRate / 100));
    }, [grossAnnualIncome, expenseRate]);

    const paybackYears = useMemo(() => {
        if (netAnnualIncome <= 0) return 0;
        return (totalInvestment / netAnnualIncome).toFixed(1);
    }, [totalInvestment, netAnnualIncome]);

    const roiPercent = useMemo(() => {
        if (totalInvestment <= 0) return 0;
        return ((netAnnualIncome / totalInvestment) * 100).toFixed(1);
    }, [totalInvestment, netAnnualIncome]);

    // Construction Timeline
    const constructionTimeline = [
        {
            stage: "Етап 1",
            title: "Фундамент та дерев’яний каркас",
            status: "completed",
            date: "Виконано",
            description: "Закладено стрічково-стовпчастий фундамент з гідроізоляцією. Зведено каркас із сушої каліброваної деревини з антисептичною обробкою."
        },
        {
            stage: "Етап 2",
            title: "Утеплення та Pokryvlya",
            status: "completed",
            date: "Виконано",
            description: "Монтаж якісної покрівлі (металочерепиця з водостічною системою). Повне утеплення стін та даху мінеральною ватою щільністю 135 кг/м³ (200 мм)."
        },
        {
            stage: "Етап 3",
            title: "Енергозберігаючі Вікна та Фасад",
            status: "completed",
            date: "Виконано",
            description: "Встановлено панорамні 6-камерні металопластикові вікна з 3-камерним склопакетом. Зовнішній фасад повністю оздоблений комбінацією планкена та металу."
        },
        {
            stage: "Етап 4",
            title: "Ділянка, Комунікації та Альтанка",
            status: "completed",
            date: "Виконано",
            description: "Власна свердловина/колодязь, автономна каналізація (септик з біоочищенням). Огородження периметра парканом, відкатні ворота та затишна альтанка."
        },
        {
            stage: "Етап 5",
            title: "Внутрішнє оздоблення (Поточний стан ~70%)",
            status: "in_progress",
            date: "В процесі / Під готовий дизайн",
            description: "Будинок підготовлений до розведення сантехніки, електрики та чистового оздоблення (чернова підлога, готові перегородки). Можливість вибору власного дизайну."
        }
    ];

    // Technical Specs
    const technicalSpecs = [
        {
            category: "Конструкція та Стіни",
            icon: <Hammer className="text-emerald-500" size={24} />,
            items: [
                { label: "Загальна площа", val: "127 м²" },
                { label: "Площа тераси", val: "7.8 м²" },
                { label: "Каркас", val: "Сухий калібрований брус" },
                { label: "Утеплення", val: "Мінвата 200 мм (стіни та дах)" },
                { label: "Покрівля", val: "Матова металочерепиця 0.5 мм" }
            ]
        },
        {
            category: "Комунікації та Автономність",
            icon: <Droplets className="text-blue-500" size={24} />,
            items: [
                { label: "Водопостачання", val: "Власний колодязь / Свердловина" },
                { label: "Каналізація", val: "Автономний септик біоочищення" },
                { label: "Електропостачання", val: "Підведено (3 фази, 15 кВт)" },
                { label: "Опалення", val: "Проект під електрокотел / камін" },
                { label: "Інтернет", val: "Оптоволокно (швидкісний)" }
            ]
        },
        {
            category: "Вікна та Енергоефективність",
            icon: <Zap className="text-amber-500" size={24} />,
            items: [
                { label: "Профіль вікон", val: "6-камерний енергозберігаючий" },
                { label: "Склопакет", val: "3-камерний з напиленням i-glass" },
                { label: "Панорами", val: "Вітальня та спальня другого поверху" },
                { label: "Клас енергоефективності", val: "A+ (мінімальні витрати)" },
                { label: "Паро/Гідроізоляція", val: "Мембрани Strotex / Tyvek" }
            ]
        },
        {
            category: "Ділянка та Благоустрій",
            icon: <TreePine className="text-emerald-600" size={24} />,
            items: [
                { label: "Площа ділянки", val: "3.9 сотки (приватна власність)" },
                { label: "Огорожа", val: "Паркан по всьому периметру" },
                { label: "Вхідна група", val: "Відкатні ворота + хвіртка" },
                { label: "Додатково", val: "Дерев'яна альтанка, замощена зона" },
                { label: "Під'їзд", val: "Всесезонний асфальтований/гравійний" }
            ]
        }
    ];

    // Floor Plans
    const floorPlans = {
        first: {
            title: "1-й Поверх (Зона відпочинку та загальний простір)",
            area: "≈ 75 м²",
            rooms: [
                { name: "Простора вітальня-кухня (друге світло)", space: "38.5 м²" },
                { name: "Гостьова спальня / Кабінет", space: "14.2 м²" },
                { name: "Санвузол та душова", space: "6.8 м²" },
                { name: "Технічне приміщення / Котельня", space: "4.5 м²" },
                { name: "Передпокій та хол", space: "11.0 м²" },
                { name: "Зовнішня відкрита тераса", space: "7.8 м²" }
            ],
            description: "Відкрите просторе планування з високими стелями та високими вікнами. Панорамний вихід безпосередньо на терасу та у двір."
        },
        second: {
            title: "2-й Поверх (Приватна спальна зона)",
            area: "≈ 52 м²",
            rooms: [
                { name: "Головна Master-спальня з балконом", space: "22.4 м²" },
                { name: "Затишна друга спальня", space: "16.1 м²" },
                { name: "Другий санвузол / Ванна кімната", space: "7.5 м²" },
                { name: "Гардеробна / Хол", space: "6.0 м²" }
            ],
            description: "Затишне приватне планування для господарів та гостей. З вікон відкривається захоплюючий вид на гори Карпати."
        }
    };

    // FAQ Data
    const faqList = [
        {
            q: "В якому стані продається будинок і що входить у вартість $71,000?",
            a: "Будинок продається у стані готовності ~70%. Повністю завершено зовнішній фасад, дах, утеплення (200мм мінвата), встановлено преміальні панорамні вікна, паркан, ворота, альтанку. Зроблено власну свердловину та автономний септик. Всередині будинок підготовлений до чистових робіт та розведення комунікацій."
        },
        {
            q: "Скільки потрібно інвестувати, щоб завершити ремонт 'під ключ'?",
            a: "За нашими розрахунками та кошторисом партнерських будівельних бригад, повне чистове оздоблення з меблями та технікою коштуватиме орієнтовно від $12,000 до $18,000 залежно від обраного класу матеріалів. Ми можемо надати перевірену бригаду."
        },
        {
            q: "Який статус має земельна ділянка?",
            a: "Земельна ділянка площею 3.9 сотки перебуває у приватній власності. Кадастровий номер та всі юридичні документи повністю готові до переоформлення у нотаріуса."
        },
        {
            q: "Чи підходить будинок для здачі в оренду та яка окупність?",
            a: "Так, це ідеальний формат бізнесу. Завдяки розташуванню між Буковелем (20 хв) та Драгобратом (25 хв), туристичний попит високий як взимку, так і влітку. Очікуваний річний дохід від оренди становить $20,000 - $30,000, що забезпечує окупність за 3-4 роки."
        },
        {
            q: "Чи можна керувати орендою дистанційно?",
            a: "Так, у регіоні працюють професійні управляючі компанії, які беруть на себе зустріч гостей, прибирання, маркетинг та обслуговування за 15-20% від доходу."
        }
    ];

    // reCaptcha
    const { executeRecaptcha } = useGoogleReCaptcha();

    // send offer

    const handleFormSubmit = useCallback( async (e) => {
        e.preventDefault();

        //
        if (!executeRecaptcha) {
            console.error('reCAPTCHA ще не завантажилася');
            return;
        }

        // 2. Генеруємо токен для конкретної дії (наприклад, 'login' або 'submit_form')
        const token = await executeRecaptcha('sell_house');

        if (!token) {
            console.error('Не вдалося отримати токен reCAPTCHA');
            return;
        }

        // console.log('Отримано v3 токен:', token);

        const formData = new FormData(e.target);

        const name = formData.get('name');
        const phone = formData.get('phone');
        const date = formData.get('date');
        const note = formData.get('note');
        const message = `<b>Нова заявка - Продаж Будинку</b>
                            👤 <b>Ім'я:</b> ${name}
                            📞 <b>Телефон:</b> ${phone}
                            📅 <b>Дата візиту:</b> ${date}
                            🗒️ <b>Примітка:</b> ${note}`;

        formData.append('text', message);
        // formData.append('parse_mode', 'HTML');
        // formData.append('recaptchaToken', token);

        try {

            const response = await fetch('https://myoffice.cv/sending/telegram.php', {
                method: 'POST',
                body: formData
            });

            const result = await response.json();

            if (result.ok) {
                localStorage.setItem('form_submitted_2', 'true');
            } else {
                alert('Помилка: ' + result.message);
            }

            window.location.reload();
        } catch (error) {
            console.error('Помилка відправки:', error);
            console.error('Помилка відправки:', error);
        }

        setFormSubmitted(true);
        localStorage.setItem('form_submitted_2', 'true');

        setTimeout(() => {
            // setFormSubmitted(false);
            setIsModalOpen(false);
            setFormData({ name: '', phone: '', date: '', message: '' });
            // localStorage.setItem('form_submitted_2', 'true');
        }, 2500);
    }, [executeRecaptcha]);

    const location = useLocation();

    useEffect(() => {

        const status = localStorage.getItem('form_submitted_2');
        if (status === 'true') {
            setFormSubmitted(true);
        }

        // Отримуємо хеш з адреси (наприклад, "#about")
        const hash = window.location.hash.split('#')[2];
        if (hash) {
            const element = document.getElementById(hash);
            if (element) {
                element.scrollIntoView({ behavior: 'smooth' });
            }
        }
    }, [location]);

    return (
        <div className="min-h-screen bg-slate-50 font-sans text-slate-800 antialiased selection:bg-emerald-500 selection:text-white">

            {/* TOP NOTIFICATION BAR */}
            <div className="bg-emerald-950 text-emerald-200 text-xs sm:text-sm py-2 px-4 text-center font-medium border-b border-emerald-800/40 flex items-center justify-center gap-2">
                <Sparkles size={16} className="text-emerald-400 animate-spin-slow shrink-0" />
                <span>Ексклюзивна пропозиція в Ясінях — будинок 127 м² з ділянкою за <strong>$71,000</strong></span>
            </div>

            {/* HEADER / HERO SECTION */}
            <header className="relative bg-gradient-to-br from-emerald-950 via-slate-900 to-emerald-900 text-white pt-12 pb-24 px-4 sm:px-6 lg:px-8 overflow-hidden shadow-2xl">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(16,185,129,0.15),transparent_50%)] pointer-events-none" />

                <div className="max-w-6xl mx-auto relative z-10">

                    {/* Top Branding / Contact */}
                    <div className="flex items-center justify-between mb-10 pb-6 border-b border-white/10">
                        <div className="flex items-center gap-3">
                            {/*<div className="w-10 h-10 rounded-xl bg-emerald-500 flex items-center justify-center text-slate-950 font-black text-xl shadow-lg shadow-emerald-500/30">*/}
                            {/*    H*/}
                            {/*</div>*/}
                            <div>
                                <span className="text-xl font-bold tracking-tight text-white block leading-none">Hlushman House</span>
                                <span className="text-xs text-emerald-400 font-medium tracking-wide">Карпати • Ясіня</span>
                            </div>
                        </div>

                        <div className="hidden sm:flex items-center gap-4">
                            <Link
                                to="https://www.instagram.com/hlushman_house"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 text-slate-300 hover:text-white text-sm font-medium transition"
                            >
                                <Image size={18} className="text-pink-400" />
                                <span>@hlushman_house</span>
                            </Link>
                            <Link
                                to="tel:0686137593"
                                className="bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 border border-emerald-500/40 px-4 py-2 rounded-xl text-sm font-bold flex items-center gap-2 transition"
                            >
                                <Phone size={16} />
                                <span>068-61-37-593</span>
                            </Link>
                        </div>
                    </div>

                    {/* Hero Content */}
                    <div className="grid lg:grid-cols-3 gap-10 items-center">
                        <div className="lg:col-span-2">
                            <div className="inline-flex items-center gap-2 bg-emerald-500/20 border border-emerald-400/30 text-emerald-300 px-4 py-1.5 rounded-full text-xs sm:text-sm font-semibold mb-6 backdrop-blur">
                                <span className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse"></span>
                                Готовність ~70% — готові документи під угоду
                            </div>

                            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight mb-6 leading-[1.15]">
                                Енергоефективний Будинок у Серці Карпат
                            </h1>

                            <p className="text-lg sm:text-xl text-slate-300 mb-8 max-w-2xl leading-relaxed">
                                Сучасний каркасний котедж площею <strong>127 м²</strong> на ділянці <strong>3.9 сотки</strong>. Ідеальна локація між курортами Буковель та Драгобрат для власного проживання або орендного бізнесу.
                            </p>

                            {/* Key Quick Badges */}
                            <div className="flex flex-wrap gap-3 text-sm text-slate-200 mb-8">
                                <div className="bg-white/10 backdrop-blur-md border border-white/10 px-4 py-2.5 rounded-xl flex items-center gap-2.5">
                                    <Maximize className="text-emerald-400" size={18} />
                                    <span>Площа: <strong>127 м²</strong></span>
                                </div>
                                <div className="bg-white/10 backdrop-blur-md border border-white/10 px-4 py-2.5 rounded-xl flex items-center gap-2.5">
                                    <Layers className="text-emerald-400" size={18} />
                                    <span>Тераса: <strong>7.8 м²</strong></span>
                                </div>
                                <div className="bg-white/10 backdrop-blur-md border border-white/10 px-4 py-2.5 rounded-xl flex items-center gap-2.5">
                                    <Home className="text-emerald-400" size={18} />
                                    <span>Ділянка: <strong>3.9 сотки</strong></span>
                                </div>
                                <div className="bg-white/10 backdrop-blur-md border border-white/10 px-4 py-2.5 rounded-xl flex items-center gap-2.5">
                                    <MapPin className="text-emerald-400" size={18} />
                                    <span>смт. Ясіня</span>
                                </div>
                            </div>

                            <div className="flex flex-wrap items-center gap-4">
                                <button
                                    onClick={() => setIsModalOpen(true)}
                                    className="bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-extrabold px-8 py-4 rounded-xl shadow-lg shadow-emerald-500/30 flex items-center gap-3 transition transform hover:-translate-y-0.5"
                                >
                                    <Calendar size={20} />
                                    <span>Записатися на перегляд</span>
                                </button>
                                <Link
                                    to="/sell-house#calculator"
                                    className="bg-white/10 hover:bg-white/20 text-white font-semibold px-6 py-4 rounded-xl border border-white/15 flex items-center gap-2 transition"
                                >
                                    <Calculator size={20} className="text-emerald-400" />
                                    <span>Розрахувати доходність</span>
                                </Link>
                            </div>
                        </div>

                        {/* Price & Action Box Card */}
                        <div className="bg-white text-slate-900 p-8 rounded-3xl shadow-2xl border border-slate-100 relative">
                            <div className="absolute -top-3 right-6 bg-emerald-600 text-white text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full shadow">
                                Вигідна інвестиція
                            </div>

                            <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Фіксована вартість</span>
                            <div className="text-4xl sm:text-5xl font-black text-emerald-700 mt-2 mb-1">
                                $71 000
                            </div>
                            <div className="text-sm font-semibold text-slate-500 mb-6">
                                ≈ 3,187,900 грн • 559 $/м²
                            </div>

                            <div className="space-y-3 pt-4 border-t border-slate-100">
                                <Link
                                    to="tel:0686137593"
                                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3.5 px-4 rounded-xl flex items-center justify-center gap-2 transition duration-200 shadow-md shadow-emerald-600/20"
                                >
                                    <Phone size={18} />
                                    068-61-37-593
                                </Link>

                                <button
                                    onClick={() => setIsModalOpen(true)}
                                    className="w-full bg-slate-900 hover:bg-slate-800 text-white font-bold py-3.5 px-4 rounded-xl flex items-center justify-center gap-2 transition duration-200"
                                >
                                    <Send size={18} className="text-emerald-400" />
                                    Надіслати запит
                                </button>

                                <Link
                                    to="https://www.instagram.com/hlushman_house"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="w-full bg-slate-100 hover:bg-slate-200 text-slate-800 font-semibold py-3 px-4 rounded-xl flex items-center justify-center gap-2 transition border border-slate-200 text-sm"
                                >
                                    <Image size={18} className="text-pink-600" />
                                    Instagram Профіль
                                </Link>
                            </div>
                        </div>
                    </div>

                </div>
            </header>

            {/* INVESTMENT METRICS BANNER */}
            <section className="max-w-6xl mx-auto -mt-10 px-4 sm:px-6 lg:px-8 relative z-20">
                <div className="bg-white rounded-3xl shadow-xl p-6 sm:p-8 grid md:grid-cols-3 gap-6 border border-slate-100 divide-y md:divide-y-0 md:divide-x divide-slate-100">

                    <div className="flex items-start gap-4">
                        <div className="p-3.5 bg-emerald-100 text-emerald-700 rounded-2xl shrink-0">
                            <TrendingUp size={30} />
                        </div>
                        <div>
                            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Очікуваний річний дохід</h3>
                            <p className="text-2xl font-black text-slate-900 mt-1">$22,000 – $30,000</p>
                            <p className="text-xs text-slate-500 mt-1">Високий туристський попит цілий рік</p>
                        </div>
                    </div>

                    <div className="flex items-start gap-4 pt-4 md:pt-0 md:pl-6">
                        <div className="p-3.5 bg-blue-100 text-blue-700 rounded-2xl shrink-0">
                            <DollarSign size={30} />
                        </div>
                        <div>
                            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Середня добова оренда</h3>
                            <p className="text-xl font-black text-slate-900 mt-1">120–180 $ <span className="text-sm font-normal text-slate-500">/зима</span></p>
                            <p className="text-xl font-black text-slate-900">80–120 $ <span className="text-sm font-normal text-slate-500">/літо</span></p>
                        </div>
                    </div>

                    <div className="flex items-start gap-4 pt-4 md:pt-0 md:pl-6">
                        <div className="p-3.5 bg-amber-100 text-amber-700 rounded-2xl shrink-0">
                            <Calendar size={30} />
                        </div>
                        <div>
                            <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider">Розрахункова окупність</h3>
                            <p className="text-2xl font-black text-slate-900 mt-1">3.5 – 4 роки</p>
                            <p className="text-xs text-slate-500 mt-1">180–220 днів завантаженості на рік</p>
                        </div>
                    </div>

                </div>
            </section>

            {/* MAIN CONTAINER */}
            <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 space-y-20">

                {/* INTERACTIVE GALLERY SECTION */}
                <section className="bg-white p-8 sm:p-10 rounded-3xl shadow-sm border border-slate-200/80">
                    <div className="flex flex-col lg:flex-row lg:items-end justify-between mb-8 gap-4">
                        <div>
                            <div className="flex items-center gap-2 text-emerald-600 font-bold text-xs uppercase tracking-wider mb-2">
                                <Camera size={18} />
                                <span>Фотогалерея об'єкта</span>
                            </div>
                            <h2 className="text-3xl font-black text-slate-900">Перегляд будинку та території</h2>
                            <p className="text-slate-500 text-sm mt-1">Натисніть на будь-яке фото для повноекранного перегляду</p>
                        </div>

                        {/* Filter Buttons */}
                        <div className="flex flex-wrap gap-2">
                            {[
                                { id: 'all', label: 'Усі фото' },
                                { id: 'exterior', label: 'Екстер’єр та ділянка' },
                                { id: 'interior', label: 'Інтер’єр / Простір' },
                                { id: 'construction', label: 'Етапи будівництва' }
                            ].map(tab => (
                                <button
                                    key={tab.id}
                                    onClick={() => setGalleryFilter(tab.id)}
                                    className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-bold transition duration-200 ${
                                        galleryFilter === tab.id
                                            ? 'bg-emerald-600 text-white shadow-md'
                                            : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                    }`}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Grid */}
                    <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
                        {filteredGallery.map((img, index) => (
                            <div
                                key={img.id}
                                onClick={() => setSelectedImageIndex(index)}
                                className="group relative h-64 rounded-2xl overflow-hidden cursor-pointer bg-slate-100 border border-slate-200/80 shadow-sm transition hover:shadow-xl"
                            >
                                <img
                                    src={img.src}
                                    alt={img.title}
                                    className="w-full h-full object-cover group-hover:scale-105 transition duration-500"
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-slate-950/20 to-transparent opacity-0 group-hover:opacity-100 transition duration-300 flex items-end p-5">
                  <span className="text-white text-sm font-bold drop-shadow flex items-center gap-2">
                    <Maximize size={16} className="text-emerald-400" />
                      {img.title}
                  </span>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* LIGHTBOX MODAL WITH NAVIGATION */}
                {selectedImageIndex !== null && filteredGallery[selectedImageIndex] && (
                    <div
                        className="fixed inset-0 bg-slate-950/90 z-50 flex items-center justify-center p-4 backdrop-blur-md"
                        onClick={() => setSelectedImageIndex(null)}
                    >
                        <div
                            className="relative max-w-5xl w-full bg-slate-900 rounded-3xl overflow-hidden shadow-2xl border border-slate-800"
                            onClick={e => e.stopPropagation()}
                        >
                            {/* Close Button */}
                            <button
                                onClick={() => setSelectedImageIndex(null)}
                                className="absolute top-4 right-4 text-slate-300 hover:text-white bg-slate-800/80 hover:bg-slate-700 p-2.5 rounded-full transition z-20"
                            >
                                <X size={22} />
                            </button>

                            {/* Prev / Next Buttons */}
                            <button
                                onClick={handlePrevImage}
                                className="absolute left-4 top-1/2 -translate-y-1/2 bg-slate-800/80 hover:bg-slate-700 text-white p-3 rounded-full transition z-20"
                            >
                                <ChevronLeft size={24} />
                            </button>
                            <button
                                onClick={handleNextImage}
                                className="absolute right-4 top-1/2 -translate-y-1/2 bg-slate-800/80 hover:bg-slate-700 text-white p-3 rounded-full transition z-20"
                            >
                                <ChevronRight size={24} />
                            </button>

                            <div className="relative max-h-[75vh] flex items-center justify-center bg-black">
                                <img
                                    src={filteredGallery[selectedImageIndex].src}
                                    alt={filteredGallery[selectedImageIndex].title}
                                    className="max-h-[75vh] w-auto object-contain"
                                />
                            </div>

                            <div className="p-5 bg-slate-900 text-white flex items-center justify-between border-t border-slate-800">
                <span className="font-semibold text-sm sm:text-base text-slate-200">
                  {filteredGallery[selectedImageIndex].title}
                </span>
                                <span className="text-xs font-bold text-emerald-400 bg-emerald-950 border border-emerald-800/60 px-3 py-1 rounded-full">
                  {selectedImageIndex + 1} / {filteredGallery.length}
                </span>
                            </div>
                        </div>
                    </div>
                )}

                {/* INTERACTIVE ROI / INVESTMENT CALCULATOR */}
                <section id="calculator" className="bg-gradient-to-br from-slate-900 via-emerald-950 to-slate-900 text-white p-8 sm:p-12 rounded-3xl shadow-2xl border border-emerald-900/40 relative overflow-hidden">
                    <div className="max-w-3xl mb-10">
                        <div className="flex items-center gap-2 text-emerald-400 font-bold text-xs uppercase tracking-wider mb-2">
                            <Calculator size={18} />
                            <span>Інвестиційний калькулятор</span>
                        </div>
                        <h2 className="text-3xl sm:text-4xl font-black text-white">Розрахунок прибутковості та окупності</h2>
                        <p className="text-slate-300 mt-2 text-sm sm:text-base">
                            Налаштуйте параметри оренди та розрахуйте чистий дохід від інвестицій у котедж.
                        </p>
                    </div>

                    <div className="grid lg:grid-cols-12 gap-10 items-center">

                        {/* Controls */}
                        <div className="lg:col-span-7 space-y-6 bg-slate-800/60 backdrop-blur p-6 sm:p-8 rounded-2xl border border-slate-700/60">

                            {/* Daily Rate Slider */}
                            <div>
                                <div className="flex justify-between items-center mb-2">
                                    <label className="text-sm font-semibold text-slate-200">Середня вартість за добу ($)</label>
                                    <span className="text-lg font-bold text-emerald-400">${dailyRate} / добу</span>
                                </div>
                                <input
                                    type="range"
                                    min="60"
                                    max="250"
                                    step="5"
                                    value={dailyRate}
                                    onChange={(e) => setDailyRate(Number(e.target.value))}
                                    className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-emerald-500"
                                />
                                <div className="flex justify-between text-xs text-slate-400 mt-1">
                                    <span>$60 (Низький сезон)</span>
                                    <span>$150 (Середній)</span>
                                    <span>$250 (Зимовий пік)</span>
                                </div>
                            </div>

                            {/* Occupancy Days Slider */}
                            <div>
                                <div className="flex justify-between items-center mb-2">
                                    <label className="text-sm font-semibold text-slate-200">Завантаженість (днів на рік)</label>
                                    <span className="text-lg font-bold text-emerald-400">{occupancyDays} днів ({Math.round((occupancyDays/365)*100)}%)</span>
                                </div>
                                <input
                                    type="range"
                                    min="90"
                                    max="300"
                                    step="5"
                                    value={occupancyDays}
                                    onChange={(e) => setOccupancyDays(Number(e.target.value))}
                                    className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-emerald-500"
                                />
                                <div className="flex justify-between text-xs text-slate-400 mt-1">
                                    <span>90 днів (Песимістичний)</span>
                                    <span>180 днів (Реалістичний)</span>
                                    <span>250+ днів (Оптимістичний)</span>
                                </div>
                            </div>

                            {/* Operating Expenses Slider */}
                            <div>
                                <div className="flex justify-between items-center mb-2">
                                    <label className="text-sm font-semibold text-slate-200">Операційні витрати & Комісія управління (%)</label>
                                    <span className="text-lg font-bold text-emerald-400">{expenseRate}%</span>
                                </div>
                                <input
                                    type="range"
                                    min="10"
                                    max="35"
                                    step="1"
                                    value={expenseRate}
                                    onChange={(e) => setExpenseRate(Number(e.target.value))}
                                    className="w-full h-2 bg-slate-700 rounded-lg appearance-none cursor-pointer accent-emerald-500"
                                />
                                <div className="flex justify-between text-xs text-slate-400 mt-1">
                                    <span>10% (Самостійно)</span>
                                    <span>20% (Стандарт)</span>
                                    <span>35% (Повний сервіс)</span>
                                </div>
                            </div>

                        </div>

                        {/* Results Display */}
                        <div className="lg:col-span-5 bg-gradient-to-b from-emerald-900 to-slate-900 p-8 rounded-2xl border border-emerald-500/30 flex flex-col justify-between h-full shadow-xl">
                            <div>
                                <span className="text-xs font-bold uppercase tracking-wider text-emerald-400">Розрахований результат</span>

                                <div className="mt-4 space-y-4">
                                    <div>
                                        <span className="text-xs text-slate-400 block">Валовий дохід на рік</span>
                                        <span className="text-2xl font-bold text-white">${grossAnnualIncome.toLocaleString()}</span>
                                    </div>

                                    <div className="pt-3 border-t border-emerald-800/60">
                                        <span className="text-xs text-slate-400 block">Чистий прибуток господарю на рік</span>
                                        <span className="text-4xl font-black text-emerald-400">${netAnnualIncome.toLocaleString()}</span>
                                        <span className="text-xs text-slate-400 block mt-1">≈ {Math.round(netAnnualIncome / 12).toLocaleString()} $ / місяць</span>
                                    </div>

                                    <div className="grid grid-cols-2 gap-4 pt-4 border-t border-emerald-800/60">
                                        <div>
                                            <span className="text-xs text-slate-400 block">Термін окупності</span>
                                            <span className="text-xl font-bold text-white">{paybackYears} років</span>
                                        </div>
                                        <div>
                                            <span className="text-xs text-slate-400 block">Річний ROI</span>
                                            <span className="text-xl font-bold text-emerald-300">{roiPercent}%</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <button
                                onClick={() => setIsModalOpen(true)}
                                className="mt-8 w-full bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-bold py-3.5 px-4 rounded-xl flex items-center justify-center gap-2 transition"
                            >
                                <CheckCircle2 size={18} />
                                Отримати повний кошторис
                            </button>
                        </div>

                    </div>
                </section>

                {/* FLOOR PLANS & LAYOUT */}
                <section className="bg-white p-8 sm:p-10 rounded-3xl shadow-sm border border-slate-200/80">
                    <div className="max-w-3xl mb-8">
                        <div className="flex items-center gap-2 text-emerald-600 font-bold text-xs uppercase tracking-wider mb-2">
                            <Layers size={18} />
                            <span>Планування простору</span>
                        </div>
                        <h2 className="text-3xl font-black text-slate-900">Деталізовані поверхи та кімнати</h2>
                        <p className="text-slate-500 text-sm mt-1">Ознайомтеся з кожною зоною будинку площею 127 м²</p>
                    </div>

                    {/* Floor Tabs */}
                    <div className="flex gap-3 mb-8 border-b border-slate-200 pb-4">
                        <button
                            onClick={() => setActiveTabFloor('first')}
                            className={`px-6 py-3 rounded-xl font-bold text-sm transition flex items-center gap-2 ${
                                activeTabFloor === 'first'
                                    ? 'bg-emerald-600 text-white shadow-md'
                                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                            }`}
                        >
                            <Home size={18} />
                            <span>1-й Поверх (75 м²)</span>
                        </button>
                        <button
                            onClick={() => setActiveTabFloor('second')}
                            className={`px-6 py-3 rounded-xl font-bold text-sm transition flex items-center gap-2 ${
                                activeTabFloor === 'second'
                                    ? 'bg-emerald-600 text-white shadow-md'
                                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                            }`}
                        >
                            <Bed size={18} />
                            <span>2-й Поверх (52 м²)</span>
                        </button>
                    </div>

                    {/* Floor Detail Box */}
                    <div className="grid lg:grid-cols-12 gap-8 items-start">
                        <div className="lg:col-span-7 bg-slate-50 p-6 sm:p-8 rounded-2xl border border-slate-200/80">
                            <h3 className="text-2xl font-extrabold text-slate-900 mb-2">
                                {floorPlans[activeTabFloor].title}
                            </h3>
                            <p className="text-slate-600 text-sm mb-6 leading-relaxed">
                                {floorPlans[activeTabFloor].description}
                            </p>

                            <div className="space-y-3">
                                {floorPlans[activeTabFloor].rooms.map((room, idx) => (
                                    <div key={idx} className="flex items-center justify-between p-3.5 bg-white rounded-xl border border-slate-200/60 shadow-xs">
                    <span className="font-semibold text-slate-800 text-sm flex items-center gap-2">
                      <Check className="text-emerald-500 shrink-0" size={16} />
                        {room.name}
                    </span>
                                        <span className="font-bold text-emerald-700 text-sm bg-emerald-50 px-3 py-1 rounded-lg">
                      {room.space}
                    </span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Visual Scheme Placeholder Card */}
                        <div className="lg:col-span-5 bg-emerald-950 text-white p-8 rounded-2xl border border-emerald-800/50 flex flex-col justify-between h-full min-h-[340px]">
                            <div>
                                <span className="text-xs font-bold uppercase tracking-wider text-emerald-400">Переваги планування</span>
                                <h4 className="text-xl font-bold mt-2 mb-4">Оптимізована ергономіка</h4>
                                <ul className="space-y-3 text-sm text-slate-300">
                                    <li className="flex items-start gap-2">
                                        <CheckCircle2 size={18} className="text-emerald-400 shrink-0 mt-0.5" />
                                        <span>Друге світло у вітальні створює відчуття величезного повітряного простору.</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <CheckCircle2 size={18} className="text-emerald-400 shrink-0 mt-0.5" />
                                        <span>2 ізольовані санвузли для зручності великої родини або компанії гостей.</span>
                                    </li>
                                    <li className="flex items-start gap-2">
                                        <CheckCircle2 size={18} className="text-emerald-400 shrink-0 mt-0.5" />
                                        <span>Прямий вихід на терасу 7.8 м² для ранкової кави з видом на гори.</span>
                                    </li>
                                </ul>
                            </div>

                            <button
                                onClick={() => setIsModalOpen(true)}
                                className="mt-6 bg-white hover:bg-emerald-50 text-slate-950 font-bold py-3 px-4 rounded-xl flex items-center justify-center gap-2 transition text-sm"
                            >
                                <FileText size={16} />
                                Отримати PDF-креслення
                            </button>
                        </div>
                    </div>
                </section>

                {/* TECHNICAL SPECIFICATIONS GRID */}
                <section className="bg-white p-8 sm:p-10 rounded-3xl shadow-sm border border-slate-200/80">
                    <div className="max-w-3xl mb-10">
                        <div className="flex items-center gap-2 text-emerald-600 font-bold text-xs uppercase tracking-wider mb-2">
                            <ShieldCheck size={18} />
                            <span>Якість будівництва</span>
                        </div>
                        <h2 className="text-3xl font-black text-slate-900">Детальні технічні характеристики</h2>
                        <p className="text-slate-500 text-sm mt-1">Використано виключно сертифіковані енергоефективні матеріали</p>
                    </div>

                    <div className="grid md:grid-cols-2 gap-6">
                        {technicalSpecs.map((group, idx) => (
                            <div key={idx} className="bg-slate-50 p-6 rounded-2xl border border-slate-200/80">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="p-2.5 bg-white rounded-xl shadow-xs border border-slate-200/60">
                                        {group.icon}
                                    </div>
                                    <h3 className="text-lg font-bold text-slate-900">{group.category}</h3>
                                </div>

                                <div className="space-y-2.5">
                                    {group.items.map((item, iIndex) => (
                                        <div key={iIndex} className="flex justify-between items-center text-sm py-1 border-b border-slate-200/50 last:border-none">
                                            <span className="text-slate-600">{item.label}</span>
                                            <span className="font-semibold text-slate-900 text-right">{item.val}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* CONSTRUCTION TIMELINE SECTION */}
                <section className="bg-slate-900 text-white p-8 sm:p-12 rounded-3xl relative overflow-hidden shadow-xl">
                    <div className="max-w-3xl mb-12">
                        <div className="flex items-center gap-2 text-emerald-400 font-bold text-xs uppercase tracking-wider mb-2">
                            <History size={18} />
                            <span>Хронологія будівництва</span>
                        </div>
                        <h2 className="text-3xl sm:text-4xl font-black text-white">Виконані етапи та рівень готовності</h2>
                        <p className="text-slate-300 mt-2 text-sm sm:text-base">
                            Усі складні та фундаментальні будівельні роботи виконані. Об’єкт готовий до чистового оздоблення під ваш смак.
                        </p>
                    </div>

                    <div className="relative border-l-2 border-emerald-500/40 ml-4 sm:ml-6 space-y-8">
                        {constructionTimeline.map((item, index) => (
                            <div key={index} className="relative pl-6 sm:pl-10">
                                <div className={`absolute -left-[17px] top-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                                    item.status === 'completed'
                                        ? 'bg-emerald-500 text-slate-950 ring-4 ring-slate-900'
                                        : 'bg-amber-500 text-slate-950 ring-4 ring-slate-900 animate-pulse'
                                }`}>
                                    {item.status === 'completed' ? <Check size={16} /> : <Hammer size={16} />}
                                </div>

                                <div className="bg-slate-800/70 backdrop-blur p-6 rounded-2xl border border-slate-700/60 shadow-md">
                                    <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                                        <span className="text-xs font-bold text-emerald-400 uppercase tracking-wider">{item.stage}</span>
                                        <span className={`px-3 py-0.5 rounded-full text-xs font-bold ${
                                            item.status === 'completed'
                                                ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                                                : 'bg-amber-500/20 text-amber-300 border border-amber-500/30'
                                        }`}>
                      {item.date}
                    </span>
                                    </div>
                                    <h3 className="text-xl font-bold text-white mb-2">{item.title}</h3>
                                    <p className="text-slate-300 text-sm leading-relaxed">{item.description}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* LOCATION & INFRASTRUCTURE */}
                <section className="bg-white p-8 sm:p-12 rounded-3xl shadow-sm border border-slate-200/80">
                    <div className="max-w-3xl mb-10">
                        <span className="text-xs font-bold uppercase tracking-wider text-emerald-600">Унікальне розташування</span>
                        <h2 className="text-3xl font-black text-slate-900 mt-1 mb-3">смт. Ясіня, Закарпатська область</h2>
                        <p className="text-slate-600 text-base leading-relaxed">
                            Об'єкт розташований у мальовничій долині між двома найвідомішими гірськолижними та туристичними курортами України. Це гарантує постійний потік відпочивальників цілий рік.
                        </p>
                    </div>

                    <div className="grid md:grid-cols-3 gap-6 mb-8">
                        <div className="p-6 bg-slate-50 rounded-2xl border border-slate-200/80 flex items-center justify-between">
                            <div>
                                <div className="font-bold text-slate-900 text-lg">Буковель</div>
                                <div className="text-xs text-slate-500">Гірськолижний курорт №1</div>
                            </div>
                            <span className="px-3 py-1.5 bg-emerald-100 text-emerald-800 text-sm font-black rounded-xl">
                20 хв
              </span>
                        </div>

                        <div className="p-6 bg-slate-50 rounded-2xl border border-slate-200/80 flex items-center justify-between">
                            <div>
                                <div className="font-bold text-slate-900 text-lg">Драгобрат</div>
                                <div className="text-xs text-slate-500">Найвищий курорт України</div>
                            </div>
                            <span className="px-3 py-1.5 bg-emerald-100 text-emerald-800 text-sm font-black rounded-xl">
                25 хв
              </span>
                        </div>

                        <div className="p-6 bg-slate-50 rounded-2xl border border-slate-200/80 flex items-center justify-between">
                            <div>
                                <div className="font-bold text-slate-900 text-lg">Центр Ясіня</div>
                                <div className="text-xs text-slate-500">Магазини, кафе, вокзал</div>
                            </div>
                            <span className="px-3 py-1.5 bg-emerald-100 text-emerald-800 text-sm font-black rounded-xl">
                5 хв
              </span>
                        </div>
                    </div>

                    {/* Tourist Points */}
                    <div className="bg-emerald-50/60 p-6 rounded-2xl border border-emerald-100 grid sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                        <div className="flex items-center gap-3">
                            <Mountain className="text-emerald-700 shrink-0" size={20} />
                            <span>Маршрути на <strong>Говерлу & Петрос</strong></span>
                        </div>
                        <div className="flex items-center gap-3">
                            <Car className="text-emerald-700 shrink-0" size={20} />
                            <span>Зручний <strong>круглорічний під'їзд</strong></span>
                        </div>
                        <div className="flex items-center gap-3">
                            <Coffee className="text-emerald-700 shrink-0" size={20} />
                            <span>Поруч <strong>ресторани та колиби</strong></span>
                        </div>
                        <div className="flex items-center gap-3">
                            <Compass className="text-emerald-700 shrink-0" size={20} />
                            <span>Екологічно <strong>чиста зона</strong></span>
                        </div>
                    </div>
                </section>

                {/* FREQUENTLY ASKED QUESTIONS (FAQ) */}
                <section className="bg-white p-8 sm:p-10 rounded-3xl shadow-sm border border-slate-200/80">
                    <div className="max-w-3xl mb-8">
                        <div className="flex items-center gap-2 text-emerald-600 font-bold text-xs uppercase tracking-wider mb-2">
                            <HelpCircle size={18} />
                            <span>Популярні питання</span>
                        </div>
                        <h2 className="text-3xl font-black text-slate-900">Часті запитання покупців</h2>
                    </div>

                    <div className="space-y-4 max-w-4xl">
                        {faqList.map((item, idx) => (
                            <div
                                key={idx}
                                className="border border-slate-200/80 rounded-2xl overflow-hidden transition"
                            >
                                <button
                                    onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                                    className="w-full text-left p-5 bg-slate-50 hover:bg-slate-100 font-bold text-slate-900 flex justify-between items-center gap-4 transition text-sm sm:text-base"
                                >
                                    <span>{item.q}</span>
                                    <ChevronRight
                                        size={20}
                                        className={`text-slate-400 transition-transform duration-200 shrink-0 ${
                                            openFaq === idx ? 'rotate-90 text-emerald-600' : ''
                                        }`}
                                    />
                                </button>
                                {openFaq === idx && (
                                    <div className="p-5 bg-white text-slate-600 text-sm leading-relaxed border-t border-slate-200/60">
                                        {item.a}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </section>

                {/* CALL TO ACTION SECTION */}
                <section className="text-center bg-gradient-to-r from-emerald-600 via-teal-700 to-emerald-800 text-white rounded-3xl p-10 sm:p-16 shadow-2xl relative overflow-hidden">
                    <div className="relative z-10 max-w-2xl mx-auto">
                        <h2 className="text-3xl sm:text-4xl font-black mb-4 leading-tight">
                            Готові оглянути будинок або отримати деталі?
                        </h2>
                        <p className="text-emerald-100 text-base sm:text-lg mb-8">
                            Зв'яжіться з власником прямо зараз для бронювання перегляду або отримання додаткових фото та документації.
                        </p>

                        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                            <Link
                                to="tel:0686137593"
                                className="w-full sm:w-auto bg-white text-slate-950 hover:bg-emerald-50 font-extrabold py-4 px-8 rounded-xl flex items-center justify-center gap-3 transition shadow-lg text-lg"
                            >
                                <Phone size={22} className="text-emerald-600" />
                                068-61-37-593
                            </Link>
                            <button
                                onClick={() => setIsModalOpen(true)}
                                className="w-full sm:w-auto bg-slate-900 hover:bg-slate-800 text-white font-bold py-4 px-8 rounded-xl flex items-center justify-center gap-3 transition text-lg border border-slate-700"
                            >
                                <Calendar size={22} className="text-emerald-400" />
                                Записатися на огляд
                            </button>
                        </div>
                    </div>
                </section>

            </main>

            {/* BOOKING / INQUIRY MODAL */}
            {isModalOpen && (
                <div
                    className="fixed inset-0 bg-slate-950/80 z-50 flex items-center justify-center p-4 backdrop-blur-sm"
                    onClick={() => setIsModalOpen(null)}
                >
                    <div
                        className="relative max-w-lg w-full bg-white rounded-3xl p-8 shadow-2xl border border-slate-100"
                        onClick={e => e.stopPropagation()}
                    >
                        <button
                            onClick={() => setIsModalOpen(false)}
                            className="absolute top-5 right-5 text-slate-400 hover:text-slate-600 p-2 rounded-full transition"
                        >
                            <X size={20} />
                        </button>

                        {formSubmitted ? (
                            <div className="text-center py-8 space-y-4">
                                <div className="w-16 h-16 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto">
                                    <CheckCircle2 size={36} />
                                </div>
                                <h3 className="text-2xl font-bold text-slate-900">Заявку прийнято!</h3>
                                <p className="text-slate-600 text-sm">
                                    Дякуємо! Ми зв'яжемося з вами найближчим часом для узгодження деталей перегляду.
                                </p>
                            </div>
                        ) : (
                            <div>
                                <h3 className="text-2xl font-black text-slate-900 mb-2">Запис на перегляд</h3>
                                <p className="text-slate-500 text-sm mb-6">
                                    Залиште свої контакти та зручну дату для огляду будинку в Ясінях.
                                </p>

                                <form onSubmit={handleFormSubmit} className="space-y-4">
                                    <div>
                                        <label className="block text-xs font-bold uppercase text-slate-500 mb-1">Ваше ім'я</label>
                                        <input
                                            type="text"
                                            required
                                            name={'name'}
                                            placeholder="Олександр"
                                            value={formData.name}
                                            onChange={e => setFormData({ ...formData, name: e.target.value })}
                                            className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-xs font-bold uppercase text-slate-500 mb-1">Номер телефону</label>
                                        <IMaskInput
                                            mask="+380 (00) 000-00-00"
                                            type="tel"
                                            required
                                            name="phone"
                                            placeholder="+380 (68) 000-00-00"
                                            value={formData.phone}
                                            // onAccept повертає відформатоване значення при кожному вводі
                                            onAccept={(value) => setFormData({ ...formData, phone: value })}
                                            className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-xs font-bold uppercase text-slate-500 mb-1">Бажана дата перегляду</label>
                                        <input
                                            type="date"
                                            name={'date'}
                                            value={formData.date}
                                            onChange={e => setFormData({ ...formData, date: e.target.value })}
                                            className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-xs font-bold uppercase text-slate-500 mb-1">Коментар або запитання</label>
                                        <textarea
                                            rows={3}
                                            name={'note'}
                                            placeholder="Чи можна оглянути у вихідні?"
                                            value={formData.message}
                                            onChange={e => setFormData({ ...formData, message: e.target.value })}
                                            className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 text-sm resize-none"
                                        />
                                    </div>

                                    <button
                                        type="submit"
                                        className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3.5 px-4 rounded-xl flex items-center justify-center gap-2 transition shadow-md shadow-emerald-600/20"
                                    >
                                        <Send size={18} />
                                        Підтвердити запис
                                    </button>
                                </form>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* MOBILE QUICK ACTION BAR (STICKY AT BOTTOM) */}
            <div className="sm:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 p-3 z-40 flex gap-2 shadow-2xl">
                <Link
                    to="tel:0686137593"
                    className="flex-1 bg-emerald-600 text-white font-bold py-3 px-3 rounded-xl flex items-center justify-center gap-2 text-sm shadow-md"
                >
                    <Phone size={16} />
                    <span>Подзвонити</span>
                </Link>
                <button
                    onClick={() => setIsModalOpen(true)}
                    className="flex-1 bg-slate-900 text-white font-bold py-3 px-3 rounded-xl flex items-center justify-center gap-2 text-sm"
                >
                    <Calendar size={16} className="text-emerald-400" />
                    <span>На огляд</span>
                </button>
            </div>

            {/* FOOTER */}
            <footer className="bg-slate-950 text-slate-400 py-10 px-4 border-t border-slate-800 text-center text-sm pb-20 sm:pb-10">
                <div className="max-w-6xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
                    {/*<div className="flex items-center gap-2">*/}
                    {/*    <div className="w-6 h-6 rounded-md bg-emerald-500 flex items-center justify-center text-slate-950 font-bold text-xs">*/}
                    {/*        H*/}
                    {/*    </div>*/}
                    {/*    <span className="font-bold text-white">Hlushman House</span>*/}
                    {/*</div>*/}
                    <p>© {new Date().getFullYear()} Hlushman House. Усі права захищені.</p>
                    <div className="flex gap-4">
                        <Link to="tel:0686137593" className="hover:text-emerald-400 transition">068-61-37-593</Link>
                        <Link to="https://www.instagram.com/hlushman_house" target="_blank" rel="noopener noreferrer" className="hover:text-emerald-400 transition">Instagram</Link>
                    </div>
                </div>
            </footer>

        </div>
    );
}